import React, { useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  LinearProgress,
  List,
  ListItemButton,
  ListItemText,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import {
  useArchiveTriviaArtifactMutation,
  useGetTriviaArtifactQuery,
  useGetTriviaArtifactsQuery,
  useRestoreTriviaArtifactMutation,
} from "../../../store/api/mlbApi";
import { ARCADE_ACCENT } from "../../../utils/triviaArcade";
import TriviaEnginePanel from "./TriviaEnginePanel";
import TriviaInspector from "./TriviaInspector";
import { engineErrorMessage } from "./triviaEnginePack";

const SPORTS = [
  { value: "mlb", label: "MLB" },
  { value: "nba", label: "NBA" },
  { value: "nfl", label: "NFL" },
];

const STATUS_FILTERS = ["all", "active", "archived"];
const ORIGIN_FILTERS = ["all", "preview", "daily", "quick", "custom"];
const PAGE_SIZE = 25;
const ARCHIVE_REASONS = [
  "duplicate",
  "too_obvious",
  "name_collision",
  "bad_pair",
  "other",
];

const ACCENT_BUTTON_SX = {
  bgcolor: ARCADE_ACCENT,
  color: "#fff",
  "&:hover": { bgcolor: "#db2777" },
};

const formatListMeta = (row) =>
  [row.seenCount ?? 0, row.origin || "unknown", row.status].join(" · ");

const NativeSelectField = ({
  id,
  label,
  value,
  onChange,
  options,
  required = false,
  emptyOption,
}) => (
  <TextField
    select
    required={required}
    fullWidth
    size="small"
    id={id}
    label={label}
    value={value}
    onChange={(event) => onChange(event.target.value)}
    SelectProps={{ native: true }}
    sx={{ minWidth: 140 }}
  >
    {emptyOption != null && <option value="">{emptyOption}</option>}
    {options.map((code) => (
      <option key={code} value={code}>
        {code}
      </option>
    ))}
  </TextField>
);

const formatInspectorField = (value) => {
  if (value == null || typeof value !== "object" || Array.isArray(value)) {
    return value;
  }
  return Object.entries(value)
    .filter(([, entry]) => entry != null)
    .map(([key, entry]) => `${key} ${entry}`)
    .join(", ");
};

const toInspectorQuestion = (artifact) => {
  if (!artifact) return null;
  const inspector = artifact.inspector
    ? {
        ...artifact.inspector,
        threshold: formatInspectorField(artifact.inspector.threshold),
      }
    : artifact.inspector;
  return {
    type: artifact.format,
    category: artifact.category,
    subcategory: artifact.subcategory,
    question: artifact.snapshot?.question,
    options: artifact.snapshot?.options || [],
    inspector,
  };
};

const buildListArgs = (sport, status, origin, page) => {
  const args = { sport, page, pageSize: PAGE_SIZE };
  if (status !== "all") args.status = status;
  if (origin !== "all") args.origin = origin;
  return args;
};

const ArtifactArchiveDialog = ({
  open,
  reason,
  note,
  isSaving,
  error,
  onReasonChange,
  onNoteChange,
  onClose,
  onConfirm,
}) => (
  <Dialog
    open={open}
    onClose={isSaving ? undefined : onClose}
    disableEscapeKeyDown={isSaving}
    fullWidth
    maxWidth="xs"
  >
    <DialogTitle>Archive artifact</DialogTitle>
    <DialogContent>
      <Stack spacing={2} sx={{ mt: 1 }}>
        {error && <Alert severity="error">{error}</Alert>}
        <NativeSelectField
          id="artifact-archive-reason"
          label="Reason"
          value={reason}
          onChange={onReasonChange}
          options={ARCHIVE_REASONS}
          required
          emptyOption="Select a reason"
        />
        <TextField
          fullWidth
          size="small"
          label="Note"
          value={note}
          onChange={(event) => onNoteChange(event.target.value)}
          multiline
          minRows={2}
        />
      </Stack>
    </DialogContent>
    <DialogActions>
      <Button onClick={onClose} disabled={isSaving}>
        Cancel
      </Button>
      <Button
        variant="contained"
        onClick={onConfirm}
        disabled={!reason || isSaving}
        sx={ACCENT_BUTTON_SX}
      >
        Confirm archive
      </Button>
    </DialogActions>
  </Dialog>
);

const TriviaArtifactReview = () => {
  const [sport, setSport] = useState("mlb");
  const [status, setStatus] = useState("all");
  const [origin, setOrigin] = useState("all");
  const [selectedId, setSelectedId] = useState(null);
  const [page, setPage] = useState(1);
  const [archiveOpen, setArchiveOpen] = useState(false);
  const [archiveReason, setArchiveReason] = useState("");
  const [archiveNote, setArchiveNote] = useState("");
  const [actionError, setActionError] = useState(null);

  const listArgs = useMemo(
    () => buildListArgs(sport, status, origin, page),
    [sport, status, origin, page]
  );
  const { data, isLoading, error } = useGetTriviaArtifactsQuery(listArgs);
  const { data: detailData, isLoading: detailLoading, error: detailError } =
    useGetTriviaArtifactQuery(selectedId, { skip: !selectedId });
  const [archiveTriviaArtifact, archiveState] =
    useArchiveTriviaArtifactMutation();
  const [restoreTriviaArtifact, restoreState] =
    useRestoreTriviaArtifactMutation();

  const artifacts = data?.artifacts || [];
  const total = data?.total ?? 0;
  const pageSize = data?.pageSize ?? PAGE_SIZE;
  const currentPage = data?.page ?? page;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const canPrev = currentPage > 1;
  const canNext = currentPage < totalPages;
  const artifact = detailData?.artifact;
  const question = toInspectorQuestion(artifact);
  const listError = engineErrorMessage(error, "Failed to load artifacts");
  const selectedError = engineErrorMessage(detailError, "Failed to load artifact");

  const resetSelection = () => {
    setSelectedId(null);
    setArchiveOpen(false);
    setActionError(null);
  };

  const resetList = () => {
    setPage(1);
    resetSelection();
  };

  const handleSportChange = (_event, nextSport) => {
    if (!nextSport || nextSport === sport) return;
    setSport(nextSport);
    resetList();
  };

  const openArchive = () => {
    setArchiveReason("");
    setArchiveNote("");
    setActionError(null);
    setArchiveOpen(true);
  };

  const handleArchive = async () => {
    if (!selectedId || !archiveReason) return;
    setActionError(null);
    try {
      await archiveTriviaArtifact({
        id: selectedId,
        reason: archiveReason,
        note: archiveNote || undefined,
      }).unwrap();
      setArchiveOpen(false);
    } catch (archiveError) {
      setActionError(engineErrorMessage(archiveError, "Archive failed"));
    }
  };

  const handleRestore = async () => {
    if (!selectedId) return;
    setActionError(null);
    try {
      await restoreTriviaArtifact({ id: selectedId }).unwrap();
    } catch (restoreError) {
      setActionError(engineErrorMessage(restoreError, "Restore failed"));
    }
  };

  return (
    <Box sx={{ bgcolor: "background.default" }}>
      <Stack
        direction={{ xs: "column", md: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "flex-start", md: "center" }}
        spacing={2}
        sx={{ mb: 2 }}
      >
        <Box>
          <Typography variant="h4" fontWeight={900}>
            Trivia artifacts
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Review fingerprints, inspect why they were picked, and archive duds.
          </Typography>
        </Box>
        <ToggleButtonGroup
          value={sport}
          exclusive
          onChange={handleSportChange}
          aria-label="Trivia sport"
          size="small"
        >
          {SPORTS.map((item) => (
            <ToggleButton key={item.value} value={item.value}>
              {item.label}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
      </Stack>

      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={2}
        sx={{ mb: 2 }}
      >
        <NativeSelectField
          id="artifact-status-filter"
          label="Status"
          value={status}
          onChange={(nextStatus) => {
            setStatus(nextStatus);
            resetList();
          }}
          options={STATUS_FILTERS}
        />
        <NativeSelectField
          id="artifact-origin-filter"
          label="Origin"
          value={origin}
          onChange={(nextOrigin) => {
            setOrigin(nextOrigin);
            resetList();
          }}
          options={ORIGIN_FILTERS}
        />
      </Stack>

      {listError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {listError}
        </Alert>
      )}

      {isLoading && (
        <LinearProgress
          sx={{
            mb: 2,
            "& .MuiLinearProgress-bar": { backgroundColor: ARCADE_ACCENT },
          }}
        />
      )}

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "minmax(0, 1fr) 320px" },
          gap: 2,
          alignItems: "start",
        }}
      >
        <TriviaEnginePanel title="Fingerprints">
          {artifacts.length === 0 && !isLoading ? (
            <Typography color="text.secondary">
              No artifacts yet. Generate a preview or play a pack.
            </Typography>
          ) : (
            <List dense disablePadding>
              {artifacts.map((row) => (
                <ListItemButton
                  key={row._id}
                  aria-label={row.fingerprint}
                  selected={selectedId === row._id}
                  onClick={() => {
                    setSelectedId(row._id);
                    setActionError(null);
                  }}
                  sx={{
                    borderLeft: 3,
                    borderColor:
                      selectedId === row._id ? ARCADE_ACCENT : "transparent",
                  }}
                >
                  <ListItemText
                    primary={row.fingerprint}
                    secondary={formatListMeta(row)}
                    primaryTypographyProps={{
                      variant: "body2",
                      fontFamily: "monospace",
                      sx: { wordBreak: "break-all" },
                    }}
                  />
                </ListItemButton>
              ))}
            </List>
          )}
          {total > pageSize && (
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              spacing={1}
              sx={{ mt: 1 }}
            >
              <Typography variant="body2" color="text.secondary">
                Showing {artifacts.length} of {total}
              </Typography>
              <Stack direction="row" spacing={1}>
                <Button
                  size="small"
                  disabled={!canPrev}
                  onClick={() => {
                    setPage((prev) => Math.max(1, prev - 1));
                    resetSelection();
                  }}
                >
                  Prev
                </Button>
                <Button
                  size="small"
                  disabled={!canNext}
                  onClick={() => {
                    setPage((prev) => prev + 1);
                    resetSelection();
                  }}
                >
                  Next
                </Button>
              </Stack>
            </Stack>
          )}
        </TriviaEnginePanel>

        <TriviaEnginePanel title="Inspector">
          {detailLoading && (
            <LinearProgress
              sx={{
                mb: 2,
                "& .MuiLinearProgress-bar": { backgroundColor: ARCADE_ACCENT },
              }}
            />
          )}
          {selectedError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {selectedError}
            </Alert>
          )}
          {actionError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {actionError}
            </Alert>
          )}
          <TriviaInspector question={question} />
          {artifact?.status === "active" && (
            <Button
              variant="contained"
              onClick={openArchive}
              sx={{ ...ACCENT_BUTTON_SX, mt: 2 }}
            >
              Archive
            </Button>
          )}
          {artifact?.status === "archived" && (
            <Button
              variant="contained"
              onClick={handleRestore}
              disabled={restoreState.isLoading}
              sx={{ ...ACCENT_BUTTON_SX, mt: 2 }}
            >
              Restore
            </Button>
          )}
        </TriviaEnginePanel>
      </Box>

      <ArtifactArchiveDialog
        open={archiveOpen}
        reason={archiveReason}
        note={archiveNote}
        isSaving={archiveState.isLoading}
        error={actionError}
        onReasonChange={setArchiveReason}
        onNoteChange={setArchiveNote}
        onClose={() => setArchiveOpen(false)}
        onConfirm={handleArchive}
      />
    </Box>
  );
};

export default TriviaArtifactReview;
